/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DateTime } from 'luxon';

import { selectUserHasEitherPermissions } from '@core/stores/auth/auth.selectors';
import { selectRouterState } from '@core/stores/router/router.selectors';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';

import { getPathParamOfRoute } from '@shared/utils/router-state';

import { selectSingleSiteSiteId } from '../../../store/single-site.selectors';
import { AiInsightsSectionViewModel } from '../components/ai-insights-section/ai-insights-section.view-model';
import { ChargerDetailsGraphSectionViewModel } from '../components/charger-details-graph-section';
import { ChargerDynamicDataViewModel } from '../components/charger-dynamic-data';
import { ChargerStaticDataViewModel } from '../components/charger-static-data';
import { ChargerStatsViewModel } from '../components/charger-stats';
import { IssuesSectionViewModel } from '../components/issues-section/issues-section.view-model';
import {
  UntranslatedConnectorDetailsCardLoadingState,
  UntranslatedEventTimelineViewModel,
} from '../single-site-charger-details.view-model';
import {
  mapChargerActivityToAiInsightsSectionViewModel,
  mapChargerActivityToChargerDetailsGraphSectionViewModel,
  mapChargerActivityToChargerStatsViewModel,
  mapChargerActivityToIssuesSectionViewModel,
  mapEvseAndDetailsActivityToUntranslatedConnectorDetailsCardLoadingState,
} from './mappings';
import { SingleSiteChargerDetailsState, singleSiteChargerDetailsFeatureKey } from './single-site-charger-details.reducer';

export const selectCanManageChargerDiagnostics = selectUserHasEitherPermissions([
  PermissionStoreModelEnum.ManageAnyChargerDiagnostics,
  PermissionStoreModelEnum.ManageOwnChargerDiagnostics,
]);

export const selectSingleSiteChargerDetailsFeature =
  createFeatureSelector<SingleSiteChargerDetailsState>(singleSiteChargerDetailsFeatureKey);

export const selectChargerActivity = createSelector(selectSingleSiteChargerDetailsFeature, (state) => state.chargerActivity);
export const selectChargerActivityIsLoading = createSelector(
  selectSingleSiteChargerDetailsFeature,
  (state) => state.chargerActivityIsLoading
);
export const selectChargerActivityError = createSelector(selectSingleSiteChargerDetailsFeature, (state) => state.chargerActivityError);

export const selectEvses = createSelector(selectChargerActivity, (activity) => activity?.evses);
export const selectPowerSplitSiblingChargers = createSelector(selectChargerActivity, (activity) => activity?.powerSplitSiblingChargers);

export const selectSingleSiteChargerId = createSelector(selectRouterState, (state) => {
  return getPathParamOfRoute(state, 'chargerId');
});

export const selectChargerName = createSelector(selectChargerActivity, (activity) => {
  return activity?.chargerName;
});

export const selectChargerNameIsLoading = createSelector(selectChargerActivityIsLoading, (isLoading) => isLoading);
export const selectChargerDetailsIsLoading = createSelector(selectChargerActivityIsLoading, (isLoading) => isLoading);

export const selectEvseStates = createSelector(selectSingleSiteChargerDetailsFeature, (state) => state.evseStates);

export const selectChargerHasActiveSession = createSelector(selectChargerActivity, (activity) => {
  return activity?.chargerHasActiveSession ?? false;
});

export const selectChargerActivityTimeline = createSelector(
  selectSingleSiteChargerDetailsFeature,
  (state) => state.chargerActivityTimeline
);
export const selectChargerActivityTimelineIsLoading = createSelector(
  selectSingleSiteChargerDetailsFeature,
  (state) => state.chargerActivityTimelineIsLoading
);
export const selectChargerActivityTimelineError = createSelector(
  selectSingleSiteChargerDetailsFeature,
  (state) => state.chargerActivityTimelineError
);

export const selectEvseName = (evseId: string) => createSelector(selectEvses, (evses) => evses?.find((evse) => evse.id === evseId)?.name);
export const selectEvsePowerOverride = (evseId: string) =>
  createSelector(selectEvseStates, (evseStates) => evseStates?.[evseId]?.activity.activity?.powerOverride);
export const selectEvsePowerLimitKw = (evseId: string) =>
  createSelector(selectEvseStates, (evseStates) => evseStates?.[evseId]?.activity.activity?.evsePowerLimitKw);
export const selectEvseMaxPowerKw = (evseId: string) =>
  createSelector(selectEvseStates, (evseStates) => evseStates?.[evseId]?.activity.activity?.maxPowerKw);

export const selectEvseState = (evseId: string) => createSelector(selectEvseStates, (evseStates) => evseStates?.[evseId]);

export const selectEvseActivity = (evseId: string) =>
  createSelector(selectEvseState(evseId), (state) => {
    return state?.activity.activity;
  });

export const selectEvseActivityIsLoading = (evseId: string) =>
  createSelector(selectEvseState(evseId), (state) => {
    return state?.activity.isLoading;
  });

export const selectEvseActivityError = (evseId: string) =>
  createSelector(selectEvseState(evseId), (state) => {
    return state?.activity.error;
  });

export const selectEvseActivityTimeline = (evseId: string) =>
  createSelector(selectEvseState(evseId), (state) => {
    return state?.activityTimeline;
  });

export const selectEvseActivityTimelineIsLoading = (evseId: string) =>
  createSelector(selectEvseState(evseId), (state) => {
    return state?.activityTimelineIsLoading;
  });

export const selectEvseActivityTimelineError = (evseId: string) =>
  createSelector(selectEvseState(evseId), (state) => {
    return state?.activityTimelineError;
  });

export const selectChargerDetailsSectionVm = createSelector(
  selectChargerActivity,
  (activity): ChargerDetailsGraphSectionViewModel | undefined => {
    if (!activity) {
      return;
    }

    return mapChargerActivityToChargerDetailsGraphSectionViewModel(activity);
  }
);

export const selectChargerStaticDataVm = createSelector(selectChargerActivity, (activity): ChargerStaticDataViewModel => {
  if (!activity) {
    return {
      loading: true,
    };
  }

  return {
    imageUrl: activity.picture,
    make: activity.make,
    model: activity.model,
    maxPowerKw: activity.maxPowerKw,
    ocppVersion: activity.ocppVersion,
    serialNumber: activity.serialNumber,
    loading: false,
  };
});

export const selectChargerDynamicDataVm = createSelector(selectChargerActivity, (activity): ChargerDynamicDataViewModel => {
  if (!activity) {
    return {
      loading: true,
    };
  }

  return {
    activePowerKw: activity.activePowerKw,
    maxPowerKw: activity.importLimitKw,
    chargerOperationalStatus: activity.chargerOperationalState,
    chargerIdentity: activity.chargerIdentity,
    firmwareVersion: activity.firmwareVersion,
    loading: false,
  };
});

export const selectChargerStatsVm = createSelector(selectChargerActivity, (activity): ChargerStatsViewModel | undefined => {
  if (!activity) {
    return;
  }

  return mapChargerActivityToChargerStatsViewModel(activity);
});

export const selectIssuesSectionVm = createSelector(selectChargerActivity, (activity): IssuesSectionViewModel | undefined => {
  if (!activity) {
    return;
  }

  return mapChargerActivityToIssuesSectionViewModel(activity);
});

export const selectAiInsightsSectionVm = createSelector(selectChargerActivity, (activity): AiInsightsSectionViewModel | undefined => {
  if (!activity) {
    return;
  }

  return mapChargerActivityToAiInsightsSectionViewModel(activity);
});

export const selectUntranslatedConnectorDetailsCardLoadingState = (evseId: string) =>
  createSelector(
    selectEvses,
    selectEvseActivityIsLoading(evseId),
    selectEvseActivity(evseId),
    selectEvseActivityTimelineIsLoading(evseId),
    selectEvseActivityTimeline(evseId),
    selectUserHasEitherPermissions([
      PermissionStoreModelEnum.ManageAnyChargerBasicCommands,
      PermissionStoreModelEnum.ManageOwnChargerBasicCommands,
    ]),
    selectUserHasEitherPermissions([
      PermissionStoreModelEnum.ManageAnyChargerPowerCommand,
      PermissionStoreModelEnum.ManageOwnChargerPowerCommand,
    ]),
    selectSingleSiteSiteId,
    (
      evses,
      activityIsLoading,
      activity,
      activityTimelineIsLoading,
      activityTimeline,
      hasManageBasicCommandsPermissions,
      hasManagePowerCommandPermissions,
      siteId
    ): UntranslatedConnectorDetailsCardLoadingState => {
      return mapEvseAndDetailsActivityToUntranslatedConnectorDetailsCardLoadingState(
        evseId,
        evses!,
        activityIsLoading!,
        activity,
        activityTimelineIsLoading!,
        activityTimeline,
        hasManageBasicCommandsPermissions,
        hasManagePowerCommandPermissions,
        siteId!
      );
    }
  );

export const selectUntranslatedEventTimelineVm = createSelector(
  selectChargerActivity,
  selectChargerActivityTimeline,
  selectChargerActivityTimelineIsLoading,
  (activity, timeline, isLoading): UntranslatedEventTimelineViewModel | undefined => {
    if (!activity) {
      return;
    }

    const lastEventTimestampISO = timeline && timeline.events.length > 0 && timeline.events[timeline.events.length - 1].timestamp;
    const lastEventTimestamp = lastEventTimestampISO ? DateTime.fromISO(lastEventTimestampISO) : undefined;

    const oldestTimestampISO = timeline?.oldest;
    const oldestTimestamp = oldestTimestampISO ? DateTime.fromISO(oldestTimestampISO) : undefined;

    const hasMoreEvents = !!lastEventTimestamp && !!oldestTimestamp && lastEventTimestamp > oldestTimestamp;

    return {
      evses: activity.evses,
      chargerName: activity.chargerName,
      events: timeline?.events ?? [],
      loading: isLoading,
      infiniteScrollEnabled: true,
      hasMoreEvents,
    };
  }
);

export const selectEventTimelineIsLoading = createSelector(
  selectChargerActivityIsLoading,
  selectChargerActivityTimelineIsLoading,
  (chargerActivityIsLoading, chargerActivityTimelineIsLoading) => {
    return chargerActivityIsLoading || chargerActivityTimelineIsLoading;
  }
);
